import { useKnowledgeDocumentDataContext } from 'features/knowledgeDocumentViewer/contexts/KnowledgeDocumentDataContext'
import { useKnowledgeDocumentUXContext } from 'features/knowledgeDocumentViewer/contexts/KnowledgeDocumentUXContext'
import { useGetRolodexEntriesByLoadOptions } from 'features/rolodex'
import { useMemo } from 'react'
import { KnowledgeDocumentEntry, RolodexEntryType } from 'types'

import { OcrResultWithPage } from './useOcrResults'

export const useOcrRefsCount = (ocrResults: OcrResultWithPage[]) => {
  const { currentDocumentId } = useKnowledgeDocumentUXContext()
  const {
    siteID,
    knowledgeDocumentEntriesForSelectedDocument,
    isLoadingKnowledgeDocuments,
  } = useKnowledgeDocumentDataContext()

  const ocrResultUnitMap = useMemo(() => {
    const map = new Map<string, string | undefined>()

    ocrResults.forEach((result) => {
      const key = `${result.id}-${result.pageNumber}`

      const knowledgeDoc = knowledgeDocumentEntriesForSelectedDocument?.find(
        (document: KnowledgeDocumentEntry) =>
          document.entry.data.fields.instance_id === result.id &&
          document.entry.data.fields.page_number === result.pageNumber
      )

      map.set(key, knowledgeDoc?.entry.tags.unit)
    })

    return map
  }, [ocrResults, knowledgeDocumentEntriesForSelectedDocument])

  const uniqueUnitTags = useMemo(() => {
    const units = new Set<string>()
    ocrResultUnitMap.forEach((unit) => {
      if (unit) units.add(unit)
    })
    return Array.from(units)
  }, [ocrResultUnitMap])

  const hasUnitTags = uniqueUnitTags.length > 0

  const { data: relevantKnowledgeDocuments, isLoading: isLoadingRelevantDocs } =
    useGetRolodexEntriesByLoadOptions<KnowledgeDocumentEntry[]>({
      type: [RolodexEntryType.KNOWLEDGE_DOCUMENT],
      tags: { site: siteID, unit: uniqueUnitTags },
      limit: hasUnitTags ? 100000 : 0,
    })

  const isLoadingRefs =
    isLoadingKnowledgeDocuments || (hasUnitTags && isLoadingRelevantDocs)

  const unitRefsCountMap = useMemo(() => {
    const map = new Map<string, number>()

    uniqueUnitTags.forEach((unit) => {
      const linkedDocs =
        relevantKnowledgeDocuments?.filter(
          (entry) =>
            entry.entry.tags.unit === unit &&
            entry.entry.tags['system-document'] !== currentDocumentId
        ) ?? []
      map.set(unit, linkedDocs.length)
    })

    return map
  }, [uniqueUnitTags, relevantKnowledgeDocuments, currentDocumentId])

  const ocrResultRefsMap = useMemo(() => {
    const map = new Map<string, number>()

    ocrResults.forEach((result) => {
      const key = `${result.id}-${result.pageNumber}`
      const unitTag = ocrResultUnitMap.get(key)
      const refsCount = unitTag ? (unitRefsCountMap.get(unitTag) ?? 0) : 0
      map.set(key, refsCount)
    })

    return map
  }, [ocrResults, ocrResultUnitMap, unitRefsCountMap])

  const getLinkedDocumentsForResult = (result: OcrResultWithPage) => {
    const key = `${result.id}-${result.pageNumber}`
    const unitTag = ocrResultUnitMap.get(key)

    if (!unitTag) return []

    return (
      relevantKnowledgeDocuments?.filter(
        (entry) =>
          entry.entry.tags.unit === unitTag &&
          entry.entry.tags['system-document'] !== currentDocumentId
      ) ?? []
    )
  }

  return {
    ocrResultRefsMap,
    ocrResultUnitMap,
    getLinkedDocumentsForResult,
    isLoadingRefs,
  }
}
